import React, { useState } from "react";
import { Button, Input, LoadingSpinner } from "@components/common";
import {
  HiChevronLeft,
  HiChevronRight,
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiSearch,
  HiSortAscending,
  HiSortDescending,
} from "react-icons/hi";
import "./DataTable.css";

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  width?: string;
}

export interface DataTableAction<T> {
  label?: string;
  icon?: React.ReactNode | ((item: T) => React.ReactNode);
  onClick?: (item: T) => void;
  variant?: "primary" | "secondary" | "danger" | "success";
  disabled?: (item: T) => boolean;
  tooltip?: string | ((item: T) => string);
  /**
   * Renderização customizada da ação (ex: menu de contexto).
   * Quando fornecida, substitui o botão padrão.
   */
  render?: (item: T) => React.ReactNode;
}

export interface ServerDataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: DataTableAction<T>[];

  // Server-side pagination
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;

  // Server-side search
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  searchPlaceholder?: string;

  // Server-side sorting
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;

  // Loading & Empty States
  loading?: boolean;
  emptyMessage?: string;

  // Estilo
  striped?: boolean;
  hover?: boolean;
  compact?: boolean;

  // Extras
  keyExtractor: (item: T) => string | number;
  headerActions?: React.ReactNode;
  filterComponent?: React.ReactNode;
}

export function ServerDataTable<T>({
  data,
  columns,
  actions,
  pagination,
  onPageChange,
  onPageSizeChange,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Buscar...",
  sortBy,
  sortOrder,
  onSortChange,
  loading = false,
  emptyMessage = "Nenhum registro encontrado",
  striped = true,
  hover = true,
  compact = false,
  keyExtractor,
  headerActions,
  filterComponent,
}: ServerDataTableProps<T>) {
  // Estado local para o input de busca
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);

  const handleSort = (key: string) => {
    const column = columns.find((col) => col.key === key);
    if (!column?.sortable || !onSortChange) return;

    if (sortBy !== key) {
      onSortChange(key, "asc");
    } else if (sortOrder === "asc") {
      onSortChange(key, "desc");
    } else {
      onSortChange(key, "asc");
    }
  };

  const renderCellContent = (item: T, column: Column<T>) => {
    if (column.render) {
      return column.render(item);
    }
    const value = (item as any)[column.key];
    return value !== null && value !== undefined ? String(value) : "-";
  };

  if (loading) {
    return (
      <div className="data-table-loading">
        <LoadingSpinner size="large" />
        <p>Carregando dados...</p>
      </div>
    );
  }

  const startItem =
    data.length > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
  const endItem = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalItems,
  );

  return (
    <div className="data-table-container">
      {/* Header com busca e ações */}
      {(onSearchChange || headerActions || filterComponent) && (
        <div className="data-table-header">
          <div
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              flex: 1,
            }}
          >
            {onSearchChange && (
              <div className="data-table-search flex items-center">
                <HiSearch className="search-icon" />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={localSearchValue}
                  onChange={(e) => setLocalSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onSearchChange(localSearchValue);
                    }
                  }}
                />
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => onSearchChange(localSearchValue)}
                >
                  Buscar
                </Button>
              </div>
            )}
            {filterComponent && (
              <div style={{ minWidth: "250px" }} className="ml-auto">
                {filterComponent}
              </div>
            )}
          </div>
          {headerActions && (
            <div className="data-table-header-actions">{headerActions}</div>
          )}
        </div>
      )}

      {/* Informações de registros */}
      <div className="data-table-info">
        <span>
          Mostrando {startItem} até {endItem} de {pagination.totalItems}{" "}
          registros
        </span>
      </div>

      {/* Tabela */}
      <div className="data-table-wrapper">
        <table
          className={`data-table ${striped ? "striped" : ""} ${
            hover ? "hover" : ""
          } ${compact ? "compact" : ""}`}
        >
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width }}
                  className={column.sortable ? "sortable" : ""}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="th-content">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span className="sort-icon">
                        {sortBy === column.key ? (
                          sortOrder === "asc" ? (
                            <HiSortAscending />
                          ) : (
                            <HiSortDescending />
                          )
                        ) : (
                          <HiSortAscending className="sort-icon-inactive" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions && actions.length > 0 && (
                <th className="actions-column">Ações</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="empty-message"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={keyExtractor(item)}>
                  {columns.map((column) => (
                    <td key={column.key}>{renderCellContent(item, column)}</td>
                  ))}
                  {actions && actions.length > 0 && (
                    <td className="actions-cell">
                      <div className="actions-buttons">
                        {actions.map((action, index) =>
                          action.render ? (
                            <React.Fragment key={index}>
                              {action.render(item)}
                            </React.Fragment>
                          ) : (
                            <Button
                              key={index}
                              variant={action.variant || "secondary"}
                              size="small"
                              onClick={() => action.onClick?.(item)}
                              disabled={action.disabled?.(item)}
                              title={
                                typeof action.tooltip === "function"
                                  ? action.tooltip(item)
                                  : action.tooltip
                              }
                            >
                              {typeof action.icon === "function"
                                ? action.icon(item)
                                : action.icon}
                              {action.label && <span>{action.label}</span>}
                            </Button>
                          ),
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            Página {pagination.page} de {pagination.totalPages}
          </div>
          <div className="pagination-controls">
            <Button
              variant="secondary"
              size="small"
              onClick={() => onPageChange(1)}
              disabled={!pagination.hasPreviousPage}
              title="Primeira página"
            >
              <HiChevronDoubleLeft />
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.hasPreviousPage}
              title="Página anterior"
            >
              <HiChevronLeft />
            </Button>

            {/* Números de página */}
            <div className="pagination-numbers">
              {Array.from(
                { length: Math.min(5, pagination.totalPages) },
                (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={
                        pagination.page === pageNum ? "primary" : "secondary"
                      }
                      size="small"
                      onClick={() => onPageChange(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                },
              )}
            </div>

            <Button
              variant="secondary"
              size="small"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              title="Próxima página"
            >
              <HiChevronRight />
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => onPageChange(pagination.totalPages)}
              disabled={!pagination.hasNextPage}
              title="Última página"
            >
              <HiChevronDoubleRight />
            </Button>
          </div>

          {/* Seletor de tamanho de página */}
          <div className="pagination-size">
            <label>
              Itens por página:
              <select
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
